import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAUT5UfS_HPUDxq-LPptN6ukuICn155irk';

export interface LocationDetails {
  address: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

interface LocationContextType {
  location: LocationDetails | null;
  setLocation: (location: LocationDetails | null) => void;
  isLoading: boolean;
  detectLocation: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Export LocationProvider as default to avoid Fast Refresh issues
const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [location, setLocation] = useState<LocationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const detectLocation = () => {
    setIsLoading(true);

    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Reverse geocode to get address from coordinates
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();

          if (data.results && data.results[0]) {
            const result = data.results[0];
            const addressComponents = result.address_components;

            const locationData: LocationDetails = {
              address: result.formatted_address,
              latitude,
              longitude,
            };

            // Extract address components
            addressComponents.forEach((component: any) => {
              if (component.types.includes('locality')) {
                locationData.city = component.long_name;
              }
              if (component.types.includes('administrative_area_level_1')) {
                locationData.state = component.long_name;
              }
              if (component.types.includes('country')) {
                locationData.country = component.long_name;
              }
              if (component.types.includes('postal_code')) {
                locationData.postalCode = component.long_name;
              }
            });

            setLocation(locationData);
            // Store in localStorage for persistence
            localStorage.setItem('user_location', JSON.stringify(locationData));
            toast.success('Location detected successfully');
            console.log('Location detected:', locationData);
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          toast.error('Failed to get location details');
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Location access denied. Please enable location permissions.');
        } else {
          toast.error('Failed to detect location. Please try manual search.');
        }
        setIsLoading(false);
      }
    );
  };

  // Auto-detect location on mount
  useEffect(() => {
    // Check if location is already stored
    const storedLocation = localStorage.getItem('user_location');
    if (storedLocation) {
      try {
        const parsedLocation = JSON.parse(storedLocation);
        setLocation(parsedLocation);
      } catch (error) {
        console.error('Error parsing stored location:', error);
      }
    } else {
      // Auto-detect location on first visit
      detectLocation();
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{
        location,
        setLocation,
        isLoading,
        detectLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export { LocationContext, LocationProvider };
